// Pure planning for the two cemetery → deck pile primitives:
//   • Pendragon Legacy. Shuffle picked Unique cards back into the
//     decks they belong to (Atlas for Sites, Spellbook otherwise),
//     then shuffle those decks.
//   • Return to Nature. Return picked cards to the BOTTOM of the deck
//     they belong to (no shuffle).
//
// No runtime dependencies. The caller resolves piles + does the actual
// splice/push/shuffle; these functions only decide which deck each
// picked card belongs to and which pile to eligibility-test against.
// `getFullCard` lets us read a card's type when the instance lacks the
// `isSite` flag (cemetery copies sometimes do).

import { matchesFilter } from '../cardFilter';

/**
 * A picked cemetery card belongs to the Atlas iff it is a Site;
 * everything else returns to the Spellbook. Reads the live instance
 * flag first, then the catalog row via getFullCard.
 */
export function deckNameForCard(card, getFullCard = null) {
  if (card?.isSite) return 'Atlas';
  const type = card?.type ?? (getFullCard && card?.cardId ? getFullCard(card.cardId)?.type : null);
  return type === 'Site' ? 'Atlas' : 'Spellbook';
}

/**
 * Which cemetery cards an effect may pick. Honors the op's optional
 * `filter` (e.g. Pendragon's { rarity: 'Unique' }) via the canonical
 * matchesFilter so rarity/type/subtype gates all work. `ctx` carries
 * `{ getFullCard }` so catalog-only fields (rarity) resolve.
 */
export function eligibleCemeteryCards(cards, filter = null, ctx = {}) {
  if (!filter || Object.keys(filter).length === 0) return [...cards];
  return cards.filter((card) => matchesFilter(card, filter, ctx));
}

/**
 * Group the picked card ids into the deck each belongs to. Returns
 * `{ Spellbook: [card...], Atlas: [card...] }`, omitting empty groups.
 * `pileCards` is the live cemetery card list; only ids still present
 * are grouped (a card removed mid-flow is silently skipped).
 */
export function groupPicksByDeck(pileCards, pickedIds, getFullCard = null) {
  const groups = {};
  for (const id of pickedIds) {
    const card = pileCards.find((c) => c.id === id);
    if (!card) continue;
    const deck = deckNameForCard(card, getFullCard);
    (groups[deck] ||= []).push(card);
  }
  return groups;
}
